import React, { useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import Landmark from "../../../../interfaces/robot/rooms/landmark";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectSelectedRoomLandmarks } from "../../../../store/room/room-selectors";
import { selectLandmarksRestSet } from "../../../../store/room/zone-planner/zone-planner-selectors";
import useServerValidationResponseEffect from "../../../../hooks/useServerValidationResponseEffect";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import useValidatedInputEffect from "../../../../hooks/useValidatedInputEffect";
import TextInput from "../../../../components/Inputs/TextInput";
import { validateLandmarkName } from "../../../../validation/landmark-validation";

const SelectedLandmarkForm: React.FC<{ landmark: Landmark }> = ({ landmark }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchLandmark, patchLandmarksRestSet } = useSharedActions();

  const landmarkRestState = useSharedSelector((state) => selectLandmarksRestSet(state));
  const landmarks = useSharedSelector((state) => selectSelectedRoomLandmarks(state));

  const { resetToggle, removeServerError, getServerValidationResultOrNull } = useServerValidationResponseEffect({
    patchError: landmarkRestState.patchError,
    resetPatchHandler: () => dispatch(patchLandmarksRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
  });

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    landmark.name ?? "",
    (newName) =>
      validateLandmarkName(
        newName,
        landmarks.filter((lm) => lm.id !== landmark.id && lm.type === landmark.type).map((z) => z.name)
      ),
    (_) => () => {},
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle
  );

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchLandmarkNameHandler();
  };

  const patchLandmarkNameHandler = () => {
    if (landmark.name !== name && nameValidationResult.isValid) {
      dispatch(patchLandmark({ id: landmark.id, data: { name } }));
    }
  };

  const patchLandmarkNameHandlerRef = useRef(patchLandmarkNameHandler);
  patchLandmarkNameHandlerRef.current = patchLandmarkNameHandler; 

  useEffect(() => {
    return () => {
      patchLandmarkNameHandlerRef.current();
    };
  }, []);

  return (
    <form noValidate autoComplete="off" onSubmit={formSubmitHandler}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInput
            label={t("input-label-name")}
            onBlur={patchLandmarkNameHandler}
            value={name}
            onChange={nameChangeHandler}
            validationResult={nameValidationResult}
          />
        </Grid>
      </Grid>
    </form>
  );
};

export default SelectedLandmarkForm;
